import moment from 'moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { User } from './User'

export type ResourceConstructorArgs = ConstructorArgs<() => Resource>

export class Resource extends BaseRecord {
    readonly recordType = 'resource'

    name?: string
    image?: Image
    isPublic?: boolean
    seqnum?: number

    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    capacity?: number
    schedule?: number
    monEndHour?: number
    tueEndHour?: number
    wedEndHour?: number
    thuEndHour?: number
    friEndHour?: number
    satEndHour?: number
    sunEndHour?: number
    monStartHour?: number
    tueStartHour?: number
    wedStartHour?: number
    thuStartHour?: number
    friStartHour?: number
    satStartHour?: number
    sunStartHour?: number

    constructor(data?: Partial<ResourceConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator?.owner
            return
        }

        this.name = data.name ?? undefined
        this.capacity = this.propertyFromIntArg(data.capacity)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.isPublic = data.is_public ?? false
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.schedule = this.propertyFromIntArg(data.schedule)
        this.monStartHour = this.propertyFromNumberArg(data.mon_start_hour)
        this.tueStartHour = this.propertyFromNumberArg(data.tue_start_hour)
        this.wedStartHour = this.propertyFromNumberArg(data.wed_start_hour)
        this.thuStartHour = this.propertyFromNumberArg(data.thu_start_hour)
        this.friStartHour = this.propertyFromNumberArg(data.fri_start_hour)
        this.satStartHour = this.propertyFromNumberArg(data.sat_start_hour)
        this.sunStartHour = this.propertyFromNumberArg(data.sun_start_hour)
        this.monEndHour = this.propertyFromNumberArg(data.mon_end_hour)
        this.tueEndHour = this.propertyFromNumberArg(data.tue_end_hour)
        this.wedEndHour = this.propertyFromNumberArg(data.wed_end_hour)
        this.thuEndHour = this.propertyFromNumberArg(data.thu_end_hour)
        this.friEndHour = this.propertyFromNumberArg(data.fri_end_hour)
        this.satEndHour = this.propertyFromNumberArg(data.sat_end_hour)
        this.sunEndHour = this.propertyFromNumberArg(data.sun_end_hour)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<ResourceConstructorArgs>): Resource {
        return new Resource({ ...this.serializeForClone(), ...overrides })
    }

    weekDays(): string[] {
        const l: string[] = []
        const schedule = this.schedule !== undefined ? this.schedule : 0
        if (schedule !== 0) {
            let idx = 1
            for (let i = 0; i < 7; i++) if ((schedule & (idx << i)) !== 0) l.push(String(idx << i))
        }
        return l
    }

    setWeekDays(weekDays: string[]): void {
        let schedule = 0
        const intVals: number[] = []
        for (const s of weekDays) intVals.push(Number(s))
        for (const i of intVals) schedule |= i
        this.schedule = schedule
    }

    deserialize(data: any): void {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelled_date) this.cancelledDate = moment(data.cancelled_date)
        if (data.last_modified_date) this.lastModifiedDate = moment(data.last_modified_date)
    }

    jsonObject() {
        const dict = super.jsonObject()
        dict['name'] = this.name
        if (this.capacity !== undefined) dict['capacity'] = this.capacity
        if (this.image !== undefined && this.image.id !== undefined) dict['image'] = this.image.id
        else if (this.image !== undefined)
            dict['image_uri'] = this.image.uri !== undefined ? this.image.uri : ''
        dict['is_public'] = this.isPublic
        dict['schedule'] = this.schedule !== undefined ? this.schedule : 0
        dict['mon_start_hour'] = this.monStartHour !== undefined ? this.monStartHour : ''
        dict['mon_end_hour'] = this.monEndHour !== undefined ? this.monEndHour : ''
        dict['tue_start_hour'] = this.tueStartHour !== undefined ? this.tueStartHour : ''
        dict['tue_end_hour'] = this.tueEndHour !== undefined ? this.tueEndHour : ''
        dict['wed_start_hour'] = this.wedStartHour !== undefined ? this.wedStartHour : ''
        dict['wed_end_hour'] = this.wedEndHour !== undefined ? this.wedEndHour : ''
        dict['thu_start_hour'] = this.thuStartHour !== undefined ? this.thuStartHour : ''
        dict['thu_end_hour'] = this.thuEndHour !== undefined ? this.thuEndHour : ''
        dict['fri_start_hour'] = this.friStartHour !== undefined ? this.friStartHour : ''
        dict['fri_end_hour'] = this.friEndHour !== undefined ? this.friEndHour : ''
        dict['sat_start_hour'] = this.satStartHour !== undefined ? this.satStartHour : ''
        dict['sat_end_hour'] = this.satEndHour !== undefined ? this.satEndHour : ''
        dict['sun_start_hour'] = this.sunStartHour !== undefined ? this.sunStartHour : ''
        dict['sun_end_hour'] = this.sunEndHour !== undefined ? this.sunEndHour : ''
        return dict
    }

    static dataType(): DataType {
        return 'resources'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
